<template>
  <el-popover
    ref="popover"
    placement="bottom-end"
    trigger="click"
    width="320"
    popper-class="notification-popover"
    @show="onPopoverShow"
  >
    <div class="notification-list">
      <div class="notification-header">
        <span>Powiadomienia</span>
        <el-button type="text" size="mini" :loading="loading" @click="fetchSummary">
          <i class="el-icon-refresh"></i>
        </el-button>
      </div>

      <router-link to="/administrator/users" class="notification-item" @click.native="closePopover">
        <span class="notification-label">
          <i class="el-icon-user-solid"></i>
          Nowi użytkownicy
        </span>
        <el-tag v-if="counts.new_users > 0" type="danger" size="mini" effect="dark">{{ counts.new_users }}</el-tag>
        <span v-else class="muted">0</span>
      </router-link>

      <router-link to="/integrations/ftp-requests" class="notification-item" @click.native="closePopover">
        <span class="notification-label">
          <i class="el-icon-folder-opened"></i>
          Żądania FTP
        </span>
        <el-tag v-if="counts.pending_ftp > 0" type="warning" size="mini" effect="dark">{{ counts.pending_ftp }}</el-tag>
        <span v-else class="muted">0</span>
      </router-link>

      <router-link to="/integrations/integration-requests" class="notification-item" @click.native="closePopover">
        <span class="notification-label">
          <i class="el-icon-connection"></i>
          Prośby o integracje
        </span>
        <el-tag v-if="counts.pending_integration > 0" type="warning" size="mini" effect="dark">{{ counts.pending_integration }}</el-tag>
        <span v-else class="muted">0</span>
      </router-link>

      <router-link to="/mls/access-requests" class="notification-item" @click.native="closePopover">
        <span class="notification-label">
          <i class="el-icon-share"></i>
          Prośby MLS
        </span>
        <el-tag v-if="counts.pending_mls > 0" type="warning" size="mini" effect="dark">{{ counts.pending_mls }}</el-tag>
        <span v-else class="muted">0</span>
      </router-link>

      <router-link to="/crm/requests" class="notification-item" @click.native="closePopover">
        <span class="notification-label">
          <i class="el-icon-s-platform"></i>
          Prośby CRM
        </span>
        <el-tag v-if="counts.pending_crm > 0" type="warning" size="mini" effect="dark">{{ counts.pending_crm }}</el-tag>
        <span v-else class="muted">0</span>
      </router-link>

      <div v-if="totalUnread === 0" class="empty-state">
        Brak nowych powiadomień
      </div>
    </div>

    <div slot="reference" class="bell-wrapper">
      <el-badge :value="totalUnread" :hidden="totalUnread === 0" :max="99" class="bell-badge">
        <i class="el-icon-bell bell-icon" />
      </el-badge>
    </div>
  </el-popover>
</template>

<script>
import request from '@/utils/request';

const LAST_SEEN_KEY = 'admin.notifications.last_seen';
const POLL_INTERVAL_MS = 60000;

export default {
  name: 'NotificationBell',
  data() {
    return {
      counts: {
        new_users: 0,
        pending_ftp: 0,
        pending_integration: 0,
        pending_mls: 0,
        pending_crm: 0,
      },
      loading: false,
      pollTimer: null,
    };
  },
  computed: {
    totalUnread() {
      const c = this.counts;
      return (c.new_users || 0)
        + (c.pending_ftp || 0)
        + (c.pending_integration || 0)
        + (c.pending_mls || 0)
        + (c.pending_crm || 0);
    },
    sinceParam() {
      return localStorage.getItem(LAST_SEEN_KEY)
        || new Date(Date.now() - 24 * 3600 * 1000).toISOString();
    },
  },
  mounted() {
    this.fetchSummary();
    this.pollTimer = setInterval(this.fetchSummary, POLL_INTERVAL_MS);
  },
  beforeDestroy() {
    if (this.pollTimer) {
      clearInterval(this.pollTimer);
      this.pollTimer = null;
    }
  },
  methods: {
    async fetchSummary() {
      this.loading = true;
      try {
        const res = await request({
          url: '/admin/notifications/summary',
          method: 'get',
          params: { since: this.sinceParam },
        });
        const payload = (res && res.data) ? res.data : res;
        if (payload && typeof payload === 'object') {
          this.counts = {
            new_users: payload.new_users || 0,
            pending_ftp: payload.pending_ftp || 0,
            pending_integration: payload.pending_integration || 0,
            pending_mls: payload.pending_mls || 0,
            pending_crm: payload.pending_crm || 0,
          };
        }
      } catch (e) {
        // silent — polling nie powinien spamować błędami
      } finally {
        this.loading = false;
      }
    },
    onPopoverShow() {
      // Reset licznika "nowi użytkownicy" — przesuwamy "last_seen" na teraz.
      // Pending dla requestów spadnie sam po approve/reject (kolejny poll).
      localStorage.setItem(LAST_SEEN_KEY, new Date().toISOString());
      this.counts.new_users = 0;
    },
    closePopover() {
      // Navbar zostaje miedzy trasami, wiec popover nie zamknie sie sam po kliknieciu w link.
      // Bez tego dymek wisi nad nowa strona az do click-outside.
      if (this.$refs.popover && typeof this.$refs.popover.doClose === 'function') {
        this.$refs.popover.doClose();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.bell-wrapper {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  height: 100%;
  padding: 0 4px;

  .bell-icon {
    font-size: 20px;
    color: #5a5e66;
    vertical-align: middle;
  }

  /* Navbar ma overflow:hidden, a el-badge domyslnie wystaje w gore.
     Wciagamy badge w bezpieczna pozycje wzgledem ikony. */
  ::v-deep .el-badge__content.is-fixed {
    top: 14px;
    right: 6px;
    transform: translateY(0) translateX(100%);
  }
}
</style>

<style lang="scss">
/* Popover content render-uje sie do document.body, wiec styly NIE moga byc scoped.
   Kotwica: popper-class="notification-popover" w template'cie. */
.notification-popover {
  .notification-list {
    margin: -12px;

    .notification-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 10px 14px;
      border-bottom: 1px solid #ebeef5;
      font-weight: 500;
      color: #303133;
      font-size: 14px;
    }

    .notification-item {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 12px 14px;
      color: #303133;
      text-decoration: none;
      border-bottom: 1px solid #f5f5f5;
      transition: background .2s;
      font-size: 13px;

      &:hover {
        background: #f5f7fa;
      }

      &:last-of-type {
        border-bottom: none;
      }

      .notification-label {
        display: inline-flex;
        align-items: center;
        gap: 8px;

        i {
          font-size: 16px;
          color: #909399;
        }
      }

      .muted {
        color: #c0c4cc;
        font-size: 12px;
      }
    }

    .empty-state {
      padding: 12px 14px;
      text-align: center;
      color: #909399;
      font-size: 12px;
      border-top: 1px solid #f5f5f5;
    }
  }
}
</style>
